import { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Descriptions, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  useMedicalEmployeesInfinite,
  useMedicalReferrals,
  useValidateReferral,
  useIssueReferral,
  useExportReferral,
  MEDICAL_EMPLOYEES_PAGE_SIZE,
  type MedicalEmployeesListFilters,
} from '@/entities/medical/api/medicalChecksApi';
import {
  checkTypeLabels,
  checkStatusLabels,
  admissionStatusLabels,
  type MedicalCheckEmployeeRow,
  type CheckType,
  type CheckStatus,
  type AdmissionStatus,
  type ValidateReferralResult,
  type ExportReferralResult,
} from '@/entities/medical/model/medicalChecksSchema';
import { antTableScroll } from '@/shared/constants/antTable';
import type { TablePaginationConfig } from 'antd/es/table/interface';

import s from './ReferralsTab.module.scss';

const { Text } = Typography;

const ADMISSION_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  fit: 'success',
  fit_with_restrictions: 'warning',
  temporary_unfit: 'error',
  permanent_unfit: 'error',
  allowed: 'success',
  not_allowed: 'error',
  allowed_with_restrictions: 'warning',
  temporary_not_allowed: 'error',
  permanent_not_allowed: 'error',
  not_assigned: 'default',
};

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'processing' | 'default'> = {
  passed: 'success',
  assigned: 'processing',
  in_progress: 'processing',
  overdue: 'error',
  failed: 'error',
  not_passed: 'error',
  not_assigned: 'default',
};

const REFERRAL_FILTERS: MedicalEmployeesListFilters = {
  preset: 'overdue',
};

const TEMPLATE_LABELS: Record<string, string> = {
  periodic_annual_2026: 'Периодический медосмотр',
  preliminary_new_hires_q2: 'Предварительный медосмотр',
  extraordinary_incident_apr2026: 'Внеочередной медосмотр',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Черновик',
  validated: 'Проверено',
  issued: 'Выпущено',
  blocked: 'Заблокировано',
};

const ITEM_STATUS_LABELS: Record<string, string> = {
  ok: 'ОК',
  warning: 'Предупреждение',
  blocking_error: 'Ошибка',
};

export function ReferralsTab() {
  const validateReferral = useValidateReferral();
  const issueReferral = useIssueReferral();
  const exportReferral = useExportReferral();
  const { data: referralsData, isLoading: referralsLoading } = useMedicalReferrals();
  const { data, isLoading, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useMedicalEmployeesInfinite(REFERRAL_FILTERS);

  const referralOptions = useMemo(
    () =>
      (referralsData ?? []).map((r) => ({
        value: r.id,
        label: `${TEMPLATE_LABELS[r.template_code] ?? r.template_code} — ${r.employees_count} чел. (${STATUS_LABELS[r.status] ?? r.status})`,
      })),
    [referralsData],
  );

  const [pageIndex, setPageIndex] = useState(0);
  const [referralId, setReferralId] = useState('');
  const [validationResult, setValidationResult] = useState<ValidateReferralResult | null>(null);
  const [exportResult, setExportResult] = useState<ExportReferralResult | null>(null);

  const loadedPageCount = data?.pages.length ?? 0;
  const maxLoadedIndex = Math.max(0, loadedPageCount - 1);
  const viewIndex = Math.min(pageIndex, maxLoadedIndex);
  const employees = useMemo(() => data?.pages[viewIndex]?.items ?? [], [data?.pages, viewIndex]);
  const itemsOnPage = employees.length;
  const canGoForward = viewIndex < loadedPageCount - 1 || Boolean(hasNextPage);
  const paginationTotal = canGoForward
    ? (viewIndex + 1) * MEDICAL_EMPLOYEES_PAGE_SIZE + 1
    : viewIndex * MEDICAL_EMPLOYEES_PAGE_SIZE + itemsOnPage;
  const showPagination = !(isLoading && loadedPageCount === 0) && (itemsOnPage > 0 || canGoForward);

  const handleTableChange = useCallback(
    async (pag: TablePaginationConfig) => {
      const idx = (pag.current ?? 1) - 1;
      if (idx < 0 || idx === viewIndex) return;
      if (idx < loadedPageCount) {
        setPageIndex(idx);
        return;
      }
      if (idx === loadedPageCount && hasNextPage && !isFetchingNextPage) {
        await fetchNextPage();
        setPageIndex(idx);
      }
    },
    [viewIndex, loadedPageCount, hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const handleValidate = () => {
    if (!referralId.trim()) {
      message.warning('Введите ID направления');
      return;
    }
    validateReferral.mutate(
      { referralId: referralId.trim(), strictMode: true },
      {
        onSuccess: (result) => {
          setValidationResult(result);
          if (result.referral.blocking_errors_count === 0) {
            message.success('Валидация пройдена');
          } else {
            message.warning(`Блокирующих ошибок: ${result.referral.blocking_errors_count}`);
          }
        },
        onError: () => message.error('Ошибка валидации направления'),
      },
    );
  };

  const handleIssue = () => {
    if (!referralId.trim()) {
      message.warning('Введите ID направления');
      return;
    }
    issueReferral.mutate(referralId.trim(), {
      onSuccess: (result) =>
        message.success(`Направление выпущено: ${STATUS_LABELS[result.referral.status] ?? result.referral.status}`),
      onError: () => message.error('Ошибка выпуска'),
    });
  };

  const handleExport = () => {
    if (!referralId.trim()) {
      message.warning('Введите ID направления');
      return;
    }
    exportReferral.mutate(
      { referralId: referralId.trim(), format: 'r7_compatible' },
      {
        onSuccess: (result) => {
          setExportResult(result);
          message.success('Экспорт получен');
        },
        onError: () => message.error('Ошибка экспорта'),
      },
    );
  };

  const columns: ColumnsType<MedicalCheckEmployeeRow> = [
    {
      title: 'ФИО',
      dataIndex: 'full_name',
      key: 'name',
    },
    {
      title: 'Табельный №',
      dataIndex: 'tab_number',
      key: 'tab',
      width: 120,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Должность',
      key: 'pos',
      render: (_: unknown, row: MedicalCheckEmployeeRow) => row.position?.name ?? '—',
    },
    {
      title: 'Подразделение',
      key: 'dept',
      render: (_: unknown, row: MedicalCheckEmployeeRow) => row.org_unit.name,
    },
    {
      title: 'Допуск',
      dataIndex: 'current_admission_status',
      key: 'admission',
      width: 150,
      render: (val: string) => (
        <Tag color={ADMISSION_COLOR[val] ?? 'default'}>{admissionStatusLabels[val as AdmissionStatus] ?? val}</Tag>
      ),
    },
    {
      title: 'Медосмотр',
      key: 'status',
      width: 160,
      render: (_: unknown, row: MedicalCheckEmployeeRow) => {
        const check = row.current_check;
        if (!check) return <Tag>—</Tag>;
        return (
          <>
            <Tag color={STATUS_COLOR[check.status] ?? 'default'}>
              {checkStatusLabels[check.status as CheckStatus] ?? check.status}
            </Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {checkTypeLabels[check.check_type as CheckType] ?? check.check_type}
            </Text>
          </>
        );
      },
    },
  ];

  const listLoading = isLoading || (isFetching && !isFetchingNextPage);

  return (
    <>
      <Alert
        type="warning"
        showIcon
        message="Раздел в режиме просмотра для демо. Выпуск/экспорт и изменения статусов будут доступны в следующей версии."
        style={{ marginBottom: 12 }}
      />
      <div className={s.root__actions}>
        <Text strong className={s.root__title}>
          Действия с направлением
        </Text>
        <Space wrap>
          <Select
            placeholder="Выберите направление"
            value={referralId || undefined}
            onChange={(v) => setReferralId(v ?? '')}
            options={referralOptions}
            loading={referralsLoading}
            style={{ width: 480 }}
            allowClear
          />
          <Button loading={validateReferral.isPending} onClick={handleValidate}>
            Валидация
          </Button>
          <Button loading={issueReferral.isPending} onClick={handleIssue}>
            Выпустить
          </Button>
          <Button loading={exportReferral.isPending} onClick={handleExport}>
            Экспорт
          </Button>
        </Space>
      </div>

      <Table
        loading={listLoading}
        dataSource={employees}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={
          showPagination
            ? {
                simple: true,
                current: viewIndex + 1,
                pageSize: MEDICAL_EMPLOYEES_PAGE_SIZE,
                total: paginationTotal,
                showSizeChanger: false,
                disabled: isFetchingNextPage,
              }
            : false
        }
        onChange={handleTableChange}
        scroll={antTableScroll}
      />

      <Modal
        title="Результат валидации направления"
        open={!!validationResult}
        onCancel={() => setValidationResult(null)}
        footer={<Button onClick={() => setValidationResult(null)}>Закрыть</Button>}
        width={600}
      >
        {validationResult ? (
          <>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Статус">
                {STATUS_LABELS[validationResult.referral.status] ?? validationResult.referral.status}
              </Descriptions.Item>
              <Descriptions.Item label="Блокирующих ошибок">
                <Text type={validationResult.referral.blocking_errors_count > 0 ? 'danger' : undefined}>
                  {validationResult.referral.blocking_errors_count}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Предупреждений">{validationResult.referral.warnings_count}</Descriptions.Item>
            </Descriptions>
            {validationResult.items_sample.length > 0 ? (
              <>
                <Text strong style={{ display: 'block', margin: '12px 0 8px' }}>
                  Примеры проблем:
                </Text>
                {validationResult.items_sample.map((item) => (
                  <div key={item.item_id} style={{ padding: '2px 0', fontSize: 12 }}>
                    <Tag color={item.validation_status === 'blocking_error' ? 'error' : 'warning'}>
                      {item.validation_status}
                    </Tag>
                    {item.validation_details
                      ? Object.entries(item.validation_details).map(([k, v]) => (
                          <Text key={k} type="secondary">
                            {' '}
                            {k}: {String(v)}
                          </Text>
                        ))
                      : null}
                  </div>
                ))}
              </>
            ) : null}
          </>
        ) : null}
      </Modal>

      <Modal
        title="Экспорт направления"
        open={!!exportResult}
        onCancel={() => setExportResult(null)}
        footer={<Button onClick={() => setExportResult(null)}>Закрыть</Button>}
        width={700}
      >
        {exportResult ? (
          <>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Шаблон">{exportResult.referral.template_code}</Descriptions.Item>
              <Descriptions.Item label="Статус">
                {STATUS_LABELS[exportResult.referral.status] ?? exportResult.referral.status}
              </Descriptions.Item>
              <Descriptions.Item label="Сформировано">{exportResult.referral.generated_at}</Descriptions.Item>
              <Descriptions.Item label="Позиций">{exportResult.referral.items.length}</Descriptions.Item>
            </Descriptions>
            {exportResult.referral.items.length > 0 ? (
              <Table
                dataSource={exportResult.referral.items}
                rowKey="item_id"
                size="small"
                pagination={false}
                style={{ marginTop: 12 }}
                columns={[
                  { title: 'ФИО', dataIndex: 'full_name', key: 'name' },
                  { title: 'Таб. №', dataIndex: 'tab_number', key: 'tab', render: (v: string | null) => v ?? '—' },
                  {
                    title: 'Статус',
                    dataIndex: 'validation_status',
                    key: 'vs',
                    render: (v: string) => (
                      <Tag color={v === 'ok' ? 'success' : v === 'warning' ? 'warning' : 'error'}>
                        {ITEM_STATUS_LABELS[v] ?? v}
                      </Tag>
                    ),
                  },
                ]}
              />
            ) : null}
          </>
        ) : null}
      </Modal>
    </>
  );
}
